'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image } from '@/components/ui';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { authModel } from '@/models/authModel';
import { getCurrentUserPlayer } from '@/models/playerModel';
import { playerModelV2 } from '@/models/playerModelV2';
import { resolveParticipantLabel } from '@/app/[locale]/event/[eventId]/libs/participantLabel';
import { ensureClient } from '@/models/util';
import LanguageSwitcher from './LanguageSwitcher';
import GlobalSearch from './GlobalSearch';
import ThemeToggle from '@/components/theme/ThemeToggle';
import { YouRow } from './YouRow';
import { useSignOut } from './useSignOut';
import { useLeaveGuard } from '@/components/ui/dirty-guard';
import {
  SideNavItemLink,
  SideNavHeader,
  SideNavFooter,
  SideNavCollapseToggle,
  SideNavControls,
  SideNavAuthFooter,
} from './side-nav-primitives';
import { NAV_CONFIG, type NavItem, createFederationNavItems } from '@/lib/navigation';
import { hasPermission } from '@/lib/auth/permissions-client';
import type { Permission } from '@/lib/auth/permissions-client';
import { posthogUtils } from '@/lib/posthog';
import { apiGet } from '@/lib/api';
import { listedInMemberships, type MineRow } from '@/app/[locale]/memberships/libs/listed';

// Enhanced UserState to match your 4 auth types
type Federation = { id: string; name: string; slug: string | null };
type FederationBase = { id: string; name: string };

export interface UserState {
  isAuthenticated: boolean;
  email: string;
  /** The you row's name (alias, else personal name); '' until known. */
  name: string;
  photoUrl: string | null;
  myProfileHref: string;
  // Admin types
  isFederationAdmin: boolean;
  isLocationAdmin: boolean;
  isEventAdmin: boolean;
  // Federation memberships
  federations: Federation[];
  adminFederations: FederationBase[];
  locationAdminIds: string[];
  eventAdminIds: string[];
}

interface SideNavProps {
  collapsed?: boolean;
  onClose?: () => void;
  onToggleCollapse?: () => void;
  className?: string;
}

type YouPlayer = Awaited<ReturnType<typeof getCurrentUserPlayer>>;

/** Name and photo for the you row: the one-ladder label over the player row; when that read
 *  failed, the nav RPC's own first/last name through the same formatter. */
function youIdentity(
  player: YouPlayer,
  nav: { first_name?: string | null; last_name?: string | null } | null,
): Pick<UserState, 'name' | 'photoUrl'> {
  if (player) {
    return {
      name: resolveParticipantLabel({ player, isJapanese: false }),
      photoUrl: player.profile_photo ?? null,
    };
  }
  const first = nav?.first_name?.trim() ?? '';
  const last = nav?.last_name?.trim() ?? '';
  return {
    name: first || last ? playerModelV2.formatName(first, last) : '',
    photoUrl: null,
  };
}

// Helper function to convert UserState to Permission[]
function userStateToPermissions(userState: UserState): Permission[] {
  const permissions: Permission[] = ['public'];

  if (userState.isAuthenticated) {
    permissions.push('authenticated');
  }

  if (userState.isFederationAdmin) {
    permissions.push('admin');
  }

  if (userState.isLocationAdmin) {
    permissions.push('location-admin');
  }

  if (userState.isEventAdmin) {
    permissions.push('event-admin');
  }

  return permissions;
}

export default function SideNav({
  collapsed = false,
  onClose,
  onToggleCollapse,
  className = '',
}: SideNavProps) {
  const locale = useLocale();
  const pathname = usePathname();
  const t = useTranslations('Nav');
  const tAdmin = useTranslations('AdminNav');
  const router = useRouter();
  const handleLogout = useSignOut();
  const { guard } = useLeaveGuard();
  const tCommon = useTranslations('Common');

  const [userState, setUserState] = useState<UserState>({
    isAuthenticated: false,
    email: '',
    name: '',
    photoUrl: null,
    myProfileHref: '',
    isFederationAdmin: false,
    isLocationAdmin: false,
    isEventAdmin: false,
    federations: [],
    adminFederations: [],
    locationAdminIds: [],
    eventAdminIds: [],
  });

  // The Memberships link follows the app's rule (`listedOnProfile`): shown only when the player
  // holds a current, upcoming, in-grace or lapsed membership, or at least one of their clubs can sell one. False while loading or on
  // any failure. The pages themselves stay reachable by link.
  const [showMemberships, setShowMemberships] = useState(false);
  // Bumped on every load and on sign-out; a membership read that resolves under an older value is stale.
  const membershipsGen = useRef(0);

  const defaultUserState: UserState = {
    isAuthenticated: false,
    email: '',
    name: '',
    photoUrl: null,
    myProfileHref: '',
    isFederationAdmin: false,
    isLocationAdmin: false,
    isEventAdmin: false,
    federations: [],
    adminFederations: [],
    locationAdminIds: [],
    eventAdminIds: [],
  };

  // Load full user state via single RPC call
  const loadUserState = useCallback(
    async (email: string, userId: string, isMounted: () => boolean) => {
      // [NAV-DEBUG] auth-trace instrumentation — disabled (grep NAV-DEBUG).
      // const sbCookies = document.cookie
      //   .split('; ')
      //   .filter((c) => c.startsWith('sb-'))
      //   .map((c) => c.split('=')[0]);
      // console.log('[NAV-DEBUG] loadUserState START — sb cookie names:', sbCookies.join(', ') || 'NONE');
      // const rpcWatchdog = setTimeout(() => {
      //   console.log('[NAV-DEBUG] loadUserState RPC STILL PENDING after 8s — possible hang');
      // }, 8000);
      try {
        const client = ensureClient();
        // The you row's name and photo come from the player row; its failure never blocks the nav.
        const [{ data, error }, player] = await Promise.all([
          client.rpc('get_nav_context', { p_user_id: userId }),
          Promise.resolve()
            .then(() => getCurrentUserPlayer())
            .catch(() => null),
        ]);
        // [NAV-DEBUG] clearTimeout(rpcWatchdog);
        // [NAV-DEBUG] console.log('[NAV-DEBUG] loadUserState RPC settled — error:', !!error, 'hasData:', !!data);

        if (!isMounted()) return;
        if (error || !data) {
          // [NAV-DEBUG] console.log('[NAV-DEBUG] loadUserState branch -> error/!data (partial)');
          setUserState({
            ...defaultUserState,
            isAuthenticated: true,
            email,
            ...youIdentity(player, null),
          });
          return;
        }

        const navData = data as {
          username: string | null;
          first_name?: string | null;
          last_name?: string | null;
          federations: Array<{
            id: string;
            name: string;
            slug: string | null;
            default_currency: string | null;
            role: string;
          }>;
          location_admin_ids: string[];
          event_admin_ids: string[];
        };

        // Fire-and-forget: the link appears only once the answer is in; failure keeps it hidden.
        // Pinned to this account, and dropped if the user signed out or another load began meanwhile.
        const gen = ++membershipsGen.current;
        Promise.all([
          apiGet<{ memberships: MineRow[] }>('/api/app/membership/mine', {
            expectedUserId: userId,
          }),
          apiGet<{ clubs: Array<{ can_sell?: boolean }> }>('/api/app/membership/clubs', {
            expectedUserId: userId,
          }),
        ])
          .then(([mine, clubs]) => {
            if (!isMounted() || gen !== membershipsGen.current) return;
            setShowMemberships(
              mine.memberships.some(listedInMemberships) ||
                clubs.clubs.some((c) => c.can_sell === true),
            );
          })
          .catch(() => {});

        const adminFeds = navData.federations.filter((f) => f.role === 'admin');

        // [NAV-DEBUG] console.log('[NAV-DEBUG] loadUserState branch -> full success');
        setUserState({
          isAuthenticated: true,
          email,
          ...youIdentity(player, navData),
          myProfileHref: navData.username ? `/${locale}/user/${navData.username}` : '',
          isFederationAdmin: adminFeds.length > 0,
          isLocationAdmin: navData.location_admin_ids.length > 0,
          isEventAdmin: navData.event_admin_ids.length > 0,
          federations: navData.federations.map((f) => ({ id: f.id, name: f.name, slug: f.slug })),
          adminFederations: adminFeds.map((f) => ({ id: f.id, name: f.name })),
          locationAdminIds: navData.location_admin_ids,
          eventAdminIds: navData.event_admin_ids,
        });
      } catch {
        // [NAV-DEBUG] clearTimeout(rpcWatchdog);
        // [NAV-DEBUG] console.log('[NAV-DEBUG] loadUserState CATCH -> isAuthenticated=FALSE (logged-out)');
        if (isMounted()) {
          setUserState(defaultUserState);
        }
      }
    },
    [locale],
  );

  // Initialize user state + subscribe to auth changes
  useEffect(() => {
    let isMounted = true;
    let loaded = false;
    const client = ensureClient();
    // [NAV-DEBUG] mount trace — disabled (grep NAV-DEBUG).
    // const sbCookiesAtMount = document.cookie
    //   .split('; ')
    //   .filter((c) => c.startsWith('sb-'))
    //   .map((c) => c.split('=')[0]);
    // console.log(
    //   '[NAV-DEBUG] === SideNav effect MOUNT === path:',
    //   window.location.pathname,
    //   '— sb cookie names:',
    //   sbCookiesAtMount.join(', ') || 'NONE',
    // );

    const load = (email: string, userId: string) => {
      if (loaded) {
        // [NAV-DEBUG] console.log('[NAV-DEBUG] load() IGNORED (already loaded)');
        return;
      }
      loaded = true;
      // [NAV-DEBUG] console.log('[NAV-DEBUG] load() proceeding');
      loadUserState(email, userId, () => isMounted);
    };

    // Initial check
    const initialize = async () => {
      // [NAV-DEBUG] getSession watchdog + Web Locks snapshot — disabled (grep NAV-DEBUG).
      // const startedAt = performance.now();
      // const watchdog = setTimeout(() => {
      //   console.log('[NAV-DEBUG] initialize() getSession STILL PENDING after 5s');
      //   const locks = navigator.locks;
      //   if (locks?.query) {
      //     locks
      //       .query()
      //       .then((snapshot) => {
      //         const fmt = (l: { name?: string; mode?: string; clientId?: string }) =>
      //           `${l.name ?? '?'}[${l.mode ?? '?'}${l.clientId ? ` client=${l.clientId}` : ''}]`;
      //         console.log(
      //           '[NAV-DEBUG] navigator.locks @hang — held:',
      //           snapshot.held?.map(fmt).join(', ') || 'none',
      //           '| pending:',
      //           snapshot.pending?.map(fmt).join(', ') || 'none',
      //         );
      //       })
      //       .catch(() => {});
      //   }
      // }, 5000);
      const session = await authModel.getSession();
      // [NAV-DEBUG] clearTimeout(watchdog);
      // [NAV-DEBUG] console.log('[NAV-DEBUG] initialize() getSession ->', session?.user ? 'HAS-SESSION' : 'NULL');
      if (!isMounted) return;
      if (session?.user?.email) {
        load(session.user.email, session.user.id);
      }
    };
    initialize();

    // React to sign-in / sign-out events so the nav updates without a full reload
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((event, session) => {
      // [NAV-DEBUG] console.log(
      //   '[NAV-DEBUG] onAuthStateChange EVENT:', event,
      //   '— session:', session?.user ? 'HAS-SESSION' : 'NULL', 'mounted:', isMounted,
      // );
      if (!isMounted) return;
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user?.email) {
        load(session.user.email, session.user.id);
      } else if (event === 'SIGNED_OUT') {
        // [NAV-DEBUG] console.log('[NAV-DEBUG] onAuthStateChange -> SIGNED_OUT');
        loaded = false;
        membershipsGen.current++;
        setShowMemberships(false);
        setUserState(defaultUserState);
      }
    });

    return () => {
      // [NAV-DEBUG] console.log('[NAV-DEBUG] === SideNav effect CLEANUP (unmount) ===');
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [locale, loadUserState]);

  // [NAV-DEBUG] render-trace effect — disabled (grep NAV-DEBUG).
  // useEffect(() => {
  //   console.log('[NAV-DEBUG] >>> RENDERED userState.isAuthenticated =', userState.isAuthenticated);
  // }, [userState.isAuthenticated]);

  // Get federation-specific nav items
  const federationNavItems = useMemo(() => {
    return createFederationNavItems(userState.federations.slice(0, 1), locale);
  }, [userState.federations, locale]);

  // Get current active parent item for subnav display
  const activeParentItem = useMemo(() => {
    if (!pathname) return null;

    const normalizedPathname = pathname.replace(/\/$/, '');
    const userPermissions = userStateToPermissions(userState);

    // Find which parent section we're currently in
    const sortedItems = [...NAV_CONFIG].sort((a, b) => {
      const aHref = a.href.startsWith('/') ? a.href : `/${locale}/${a.href}`;
      const bHref = b.href.startsWith('/') ? b.href : `/${locale}/${b.href}`;
      return bHref.length - aHref.length;
    });

    for (const item of sortedItems) {
      if (!hasPermission(userPermissions, item.permissions)) {
        continue;
      }

      const itemHref = item.href.startsWith('/') ? item.href : `/${locale}/${item.href}`;
      const normalizedItemHref = itemHref.replace(/\/$/, '');

      // Special handling for dashboard
      if (item.key === 'dashboard') {
        const isDashboardRoute =
          normalizedPathname === `/${locale}` ||
          normalizedPathname === '' ||
          normalizedPathname === `/${locale}/`;
        if (isDashboardRoute) return null;
        continue;
      }

      if (
        normalizedPathname === normalizedItemHref ||
        normalizedPathname.startsWith(`${normalizedItemHref}/`)
      ) {
        return item;
      }
    }

    return null;
  }, [pathname, locale, userState]);

  // Process nav items with proper permissions and hierarchy
  const processNavItems = useCallback(
    (items: NavItem[]): NavItem[] => {
      const userPermissions = userStateToPermissions(userState);

      return items
        .filter((item) => {
          if (!hasPermission(userPermissions, item.permissions)) return false;
          if (item.key === 'memberships' && !showMemberships) return false;
          if (item.featureFlag && !posthogUtils.isFeatureEnabled(item.featureFlag)) return false;
          return true;
        })
        .map((item) => {
          // Replace profile href with actual user profile
          if (item.key === 'me' && !userState.myProfileHref) {
            return null; // Don't show if no profile URL
          }

          const processedItem = { ...item };

          if (item.key === 'me') {
            processedItem.href = userState.myProfileHref;
          } else if (
            !processedItem.href.startsWith('/') &&
            !processedItem.href.startsWith('http')
          ) {
            processedItem.href = `/${locale}/${processedItem.href}`;
          }

          return processedItem;
        })
        .filter((item): item is NavItem => item !== null);
    },
    [userState, locale, showMemberships],
  );

  // Get all visible navigation items (activeParentItem for React Compiler)
  const visibleNavItems = useMemo(() => {
    const mainItems = processNavItems(NAV_CONFIG);

    const dashboardItems = mainItems.filter((item) => item.key === 'dashboard');
    const otherItems = mainItems.filter((item) => item.key !== 'dashboard');
    const federationItems = processNavItems(federationNavItems);

    let allItems = [...dashboardItems, ...federationItems, ...otherItems];

    if (activeParentItem?.children && !collapsed) {
      const childItems = processNavItems(activeParentItem.children);
      // Children render directly after their parent — appending puts them under the wrong item.
      const parentIdx = allItems.findIndex((i) => i.key === activeParentItem.key);
      if (parentIdx >= 0) {
        allItems = [
          ...allItems.slice(0, parentIdx + 1),
          ...childItems,
          ...allItems.slice(parentIdx + 1),
        ];
      } else {
        allItems = [...allItems, ...childItems];
      }
    }

    return allItems;
  }, [processNavItems, federationNavItems, activeParentItem, collapsed]);

  // Active state is owned by leaves; parents only show as expanded. Without
  // this skip, a parent and its same-href child tie on longest-match.
  const activeItemKey = useMemo(() => {
    if (!pathname) return null;
    const normalizedPathname = pathname.replace(/\/$/, '');
    let best: { key: string; len: number } | null = null;
    for (const item of visibleNavItems) {
      if (item.children && item.children.length > 0) continue; // skip parents
      const normalizedHref = item.href.replace(/\/$/, '');
      const matches =
        normalizedPathname === normalizedHref ||
        normalizedPathname.startsWith(`${normalizedHref}/`);
      if (matches && (!best || normalizedHref.length > best.len)) {
        best = { key: item.key, len: normalizedHref.length };
      }
    }
    return best?.key ?? null;
  }, [pathname, visibleNavItems]);

  // Check if a nav item is active
  const isNavItemActive = useCallback(
    (item: NavItem): boolean => {
      if (!pathname) return false;

      // Special handling for dashboard routes
      if (item.key === 'dashboard') {
        const normalizedPathname = pathname.replace(/\/$/, '');
        return (
          normalizedPathname === `/${locale}` ||
          normalizedPathname === '' ||
          normalizedPathname === `/${locale}`
        );
      }

      return item.key === activeItemKey;
    },
    [pathname, locale, activeItemKey],
  );

  // Check if an item is a child of the active parent
  const isChildItem = useCallback(
    (item: NavItem): boolean => {
      return activeParentItem?.children?.some((child) => child.key === item.key) ?? false;
    },
    [activeParentItem?.children],
  );

  // Get the appropriate translation for a nav item
  const getNavItemLabel = useCallback(
    (item: NavItem): string => {
      // For federation items, use the labelKey directly (it's the federation name)
      if (item.key.startsWith('federationPage-')) {
        return item.labelKey;
      }

      // Use admin translations for admin items, but fallback to regular nav translations
      const isAdminItem = item.permissions?.includes('admin') || isChildItem(item);
      return isAdminItem ? tAdmin(item.labelKey) : t(item.labelKey);
    },
    [t, tAdmin, isChildItem],
  );

  // Render a single navigation item
  const renderNavItem = useCallback(
    (item: NavItem) => {
      const isActive = isNavItemActive(item);
      const isChild = isChildItem(item);
      const label = getNavItemLabel(item);
      const isExternal = item.href.startsWith('http');

      // Expanded parent uses softer treatment so the active child wins focal attention.
      const isExpandedParent =
        !isChild &&
        activeParentItem?.key === item.key &&
        (activeParentItem?.children?.length ?? 0) > 0;

      return (
        <SideNavItemLink
          key={item.key}
          href={item.href}
          label={label}
          icon={item.icon}
          active={isActive}
          ariaCurrent={isActive ? 'page' : undefined}
          collapsed={collapsed}
          isChild={isChild}
          comingSoon={item.comingSoon}
          comingSoonLabel={tCommon('comingSoon')}
          external={isExternal}
          openInNewTab={item.openInNewTab}
          expandedParent={isExpandedParent}
          onClick={onClose}
          prefetch={false}
        />
      );
    },
    [collapsed, onClose, isNavItemActive, isChildItem, getNavItemLabel, tCommon, activeParentItem],
  );

  return (
    <div
      className={`bg-card flex h-full flex-col space-y-4 ${collapsed ? 'px-2 py-4' : 'p-4'} ${className}`}
    >
      {/* Mobile header with logo and close button */}
      {onClose && (
        <div className="flex items-center justify-between sm:hidden">
          <SideNavHeader size="lg" showText={true} onLogoClick={onClose} />
          <button
            onClick={onClose}
            className="border-border hover:bg-surface inline-flex h-8 w-8 items-center justify-center rounded-md border"
            aria-label="Close navigation"
          >
            <span className="block h-0.5 w-3.5 -translate-y-0.5 rotate-45 bg-current" />
            <span className="block h-0.5 w-3.5 translate-y-0.5 -rotate-45 bg-current" />
          </button>
        </div>
      )}

      {/* Desktop: Logo and Collapse button */}
      <div
        className={`hidden sm:flex ${collapsed ? 'flex-col items-center space-y-2' : 'items-center justify-between'}`}
      >
        <SideNavHeader size={collapsed ? 'md' : 'lg'} showText={!collapsed} onLogoClick={onClose} />
        {onToggleCollapse && (
          <SideNavCollapseToggle
            collapsed={collapsed}
            onToggle={onToggleCollapse}
            expandLabel="Expand sidebar"
            collapseLabel="Collapse sidebar"
          />
        )}
      </div>

      {/* Search Bar */}
      {!collapsed && (
        <div>
          <GlobalSearch />
        </div>
      )}

      {/* Navigation */}
      <nav
        aria-label="Primary navigation"
        className={`flex flex-1 flex-col overflow-hidden ${collapsed ? 'items-center text-xs' : 'text-sm'}`}
      >
        <ul
          className={`flex-1 space-y-1 overflow-y-auto ${collapsed ? 'w-full overflow-x-hidden' : ''}`}
        >
          {visibleNavItems.map(renderNavItem)}
        </ul>

        {userState.isAuthenticated ? (
          /* Signed in: the you row replaces the theme and language controls and the sign-out button.
             Both live in Settings (Preferences) and the menu. */
          <SideNavFooter collapsed={collapsed}>
            <YouRow
              collapsed={collapsed}
              name={userState.name}
              email={userState.email}
              photoUrl={userState.photoUrl}
              locale={locale}
              // A menu item is not an anchor, so the guard's click capture never sees it: the
              // push itself goes through guard() (the GlobalSearch pattern).
              onSettings={() =>
                void guard(() => {
                  router.push(`/${locale}/settings`);
                  onClose?.();
                })
              }
              onSignOut={handleLogout}
            />
          </SideNavFooter>
        ) : (
          <>
            {/* Controls Section - hidden when collapsed */}
            {!collapsed && (
              <SideNavControls
                collapsed={collapsed}
                themeLabel={tCommon('theme')}
                languageLabel={tCommon('language')}
                themeToggle={<ThemeToggle collapsed={collapsed} />}
                languageSwitcher={<LanguageSwitcher />}
              />
            )}

            {/* User Section - shown in both collapsed and expanded states */}
            <SideNavFooter collapsed={collapsed}>
              <SideNavAuthFooter
                collapsed={collapsed}
                isAuthenticated={false}
                email={userState.email}
                labels={{ logout: t('logout'), login: t('login'), notLoggedIn: t('notLoggedIn') }}
                locale={locale}
                onLogout={handleLogout}
                onNavigate={onClose}
              />
            </SideNavFooter>
          </>
        )}

        {/* Buy Me a Coffee Section */}
        <div className={`${collapsed ? 'hidden' : 'mt-4 pt-1'}`}>
          <div className={`pb-1 text-center ${collapsed ? 'hidden px-0' : 'px-3'}`}>
            <a
              href="https://www.buymeacoffee.com/playpulse"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Image
                src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                alt="Buy Me A Coffee"
                width={145}
                height={40}
                className="rounded-md"
                unoptimized
              />
            </a>
          </div>
        </div>

        {/* Sponsored Section */}
        {/* <div className={`border-border border-t ${collapsed ? 'pt-1' : 'pt-1'}`}>
          <div className={`pb-1 text-center ${collapsed ? 'px-0 text-[10px]' : 'px-3 text-xs'}`}>
            <span className="text-foreground/60">{tCommon('sponsoredBy')} </span>
            <a
              href="https://www.instagram.com/stellarsquash/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-orange-500 transition-colors hover:text-orange-600"
            >
              {tCommon('stellarSports')}
            </a>
          </div>
        </div> */}
      </nav>
    </div>
  );
}
